import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Tooltip } from 'antd'
import { menuBuilderListCopy } from 'src/constants/menuBuilderCopy'
import { buildMenuBuilderListHeaderLine } from 'src/utils/menuBuilderListHeader'
import {
  RECIPE_MONITORING_EXPLAINER,
  recipeMonitoringSummaryLine,
} from 'src/utils/recipeMonitoring'

const MenuBuilderListHeader = ({
  recipes = [],
  currentPlan = 'Free',
  usageLimits = {},
  monitoringLine = null,
  archiveLine = null,
  monitoringOnly = false,
  storedCount,
}) => {
  const { menuHealth, healthPart, noPricesPart, limitPart } = useMemo(
    () =>
      buildMenuBuilderListHeaderLine({
        recipes,
        currentPlan,
        usageLimits,
        storedCount,
      }),
    [recipes, currentPlan, usageLimits, storedCount],
  )

  const [whyOpen, setWhyOpen] = useState(false)
  const summaryLine = monitoringLine
    ? recipeMonitoringSummaryLine(currentPlan)
    : null

  const accessibilityLabel = [
    monitoringLine,
    summaryLine,
    whyOpen ? RECIPE_MONITORING_EXPLAINER.activeArchived : null,
    whyOpen ? RECIPE_MONITORING_EXPLAINER.who : null,
    monitoringOnly
      ? null
      : menuHealth.hasScore
      ? `${healthPart}. Average gross profit across costed recipes only.`
      : `${healthPart}. ${menuBuilderListCopy.menuHealthEmptyHint}`,
    monitoringOnly || !noPricesPart ? null : `${noPricesPart}.`,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <CompactBar role='status' aria-label={accessibilityLabel}>
      {monitoringLine ? (
        <MonitoringLine data-testid='recipe-monitoring-header'>
          {monitoringLine}
        </MonitoringLine>
      ) : null}
      {summaryLine ? (
        <Explainer data-testid='recipe-monitoring-explainer'>
          <ExplainerBody>{summaryLine}</ExplainerBody>
          <WhyButton
            type='button'
            data-testid='recipe-monitoring-why'
            aria-expanded={whyOpen}
            onClick={() => setWhyOpen(open => !open)}
          >
            {RECIPE_MONITORING_EXPLAINER.whyLabel}
          </WhyButton>
          {whyOpen ? (
            <div data-testid='recipe-monitoring-why-body'>
              <ExplainerBody>
                {RECIPE_MONITORING_EXPLAINER.activeArchived}
              </ExplainerBody>
              <ExplainerBody>{RECIPE_MONITORING_EXPLAINER.who}</ExplainerBody>
            </div>
          ) : null}
        </Explainer>
      ) : null}
      {archiveLine ? (
        <ArchiveLine data-testid='recipe-archive-allowance'>
          {archiveLine}
        </ArchiveLine>
      ) : null}
      {monitoringOnly ? null : (
        <LineText>
          <Tooltip
            title={
              menuHealth.hasScore
                ? 'Average gross profit across costed recipes only.'
                : menuBuilderListCopy.menuHealthEmptyHint
            }
          >
            <HealthText
              $color={
                menuHealth.hasScore
                  ? menuHealth.band.color
                  : 'rgba(0, 0, 0, 0.45)'
              }
            >
              {healthPart}
            </HealthText>
          </Tooltip>
          {noPricesPart ? (
            <>
              <Separator> · </Separator>
              <LimitText data-testid='menu-builder-no-prices-count'>
                {noPricesPart}
              </LimitText>
            </>
          ) : null}
          {limitPart ? (
            <>
              <Separator> · </Separator>
              <LimitText>{limitPart}</LimitText>
            </>
          ) : null}
        </LineText>
      )}
    </CompactBar>
  )
}

const CompactBar = styled.div`
  margin: 0 5px 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`

const MonitoringLine = styled.div`
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  margin-bottom: 4px;
`

const Explainer = styled.div`
  margin: 0 0 8px;
`

const WhyButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  margin: 0 0 4px;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.65);
  text-decoration: underline;
  cursor: pointer;
`

const ExplainerBody = styled.p`
  margin: 0 0 4px;
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.65);
  white-space: normal;
`

const ArchiveLine = styled.div`
  font-size: 13px;
  line-height: 18px;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 4px;
`

const LineText = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const HealthText = styled.span`
  color: ${({ $color }) => $color};
`

const Separator = styled.span`
  color: #d9d9d9;
`

const LimitText = styled.span`
  color: rgba(0, 0, 0, 0.45);
`

export default MenuBuilderListHeader
